import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Snackbar,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { format, isValid } from 'date-fns';
import { toZonedTime } from 'date-fns-tz/toZonedTime';
import { ptBR } from 'date-fns/locale';
import MediaGallery from '../../medias/public/MediaGallery';
import { apiDateTimeToDatetimeLocal, datetimeLocalToApi } from '../../../app/date';
import { renderTextWithLinks } from './renderTextWithLinks';
import { alpha } from '@mui/material/styles';
import { patchOrderStatusHistory } from '../../../app/api';

const TZ = 'America/Sao_Paulo';

function parseEventDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return isValid(d) ? d : null;
}

const HISTORY_TEXT_PREVIEW = 400;

function displayHistoryValue(s) {
  if (s == null || String(s).trim() === '') return '\u2014';
  return String(s);
}

function previewHistoryText(s, max = HISTORY_TEXT_PREVIEW) {
  const t = String(s ?? '');
  if (t.length <= max) return t;
  return `${t.slice(0, max)}\u2026`;
}

/** Legacy: `["title","observations"]`. Current: `[{ field, from, to }]`. */
function parseFieldChanges(ev) {
  if (!ev || !ev.changed_fields) return { legacyKeys: null, changes: null };
  try {
    const arr = JSON.parse(ev.changed_fields);
    if (!Array.isArray(arr) || arr.length === 0) return { legacyKeys: null, changes: null };
    if (typeof arr[0] === 'string') {
      return { legacyKeys: arr.map((x) => String(x)), changes: null };
    }
    if (arr[0] && typeof arr[0] === 'object') {
      const rawKey = arr[0].field != null ? arr[0].field : arr[0].Field;
      if (rawKey == null) {
        return { legacyKeys: null, changes: null };
      }
      const changes = arr
        .map((row) => ({
          field: String((row.field != null ? row.field : row.Field) || ''),
          from: row.from != null ? String(row.from) : row.From != null ? String(row.From) : '',
          to: row.to != null ? String(row.to) : row.To != null ? String(row.To) : '',
        }))
        .filter((c) => c.field);
      return { legacyKeys: null, changes: changes.length ? changes : null };
    }
  } catch (_) {
    /* ignore */
  }
  return { legacyKeys: null, changes: null };
}

const fieldKeyToI18n = {
  customer_id: 'orders.history_field_customer_id',
  title: 'orders.history_field_title',
  valid_until: 'orders.history_field_valid_until',
  delivery_date: 'orders.history_field_delivery_date',
  observations: 'orders.history_field_observations',
  discount_value: 'orders.history_field_discount_value',
  created_at: 'orders.history_field_created_at',
  payment_entries: 'orders.history_field_payment_entries',
  line_item_added: 'orders.history_field_line_item_added',
  line_item_removed: 'orders.history_field_line_item_removed',
  line_item_updated: 'orders.history_field_line_item_updated',
  item_customization_fields: 'orders.history_field_item_customization_fields',
  item_custom_field_removed: 'orders.history_field_item_custom_field_removed',
  order_customization_fields: 'orders.history_field_order_customization_fields',
};

function historyFieldI18nPath(fieldKey) {
  if (fieldKeyToI18n[fieldKey]) return fieldKeyToI18n[fieldKey];
  const pairs = [
    ['line_item_added:', 'line_item_added'],
    ['line_item_removed:', 'line_item_removed'],
    ['line_item_updated:', 'line_item_updated'],
  ];
  for (const [prefix, base] of pairs) {
    if (fieldKey.startsWith(prefix)) return fieldKeyToI18n[base];
  }
  return null;
}

function groupEventsByDay(events) {
  const map = new Map();
  for (const ev of events) {
    const d = parseEventDate(ev.created_at);
    if (!d) continue;
    const z = toZonedTime(d, TZ);
    const key = format(z, 'yyyy-MM-dd');
    if (!map.has(key)) {
      map.set(key, { zoned: z, events: [] });
    }
    map.get(key).events.push({ ...ev, _zoned: z });
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([, v]) => {
      v.events.sort((x, y) => y._zoned.getTime() - x._zoned.getTime());
      return v;
    });
}

const dayEntriesSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: { xs: '10px', md: '12px' },
  pl: '18px',
};

const OrderHistoryTimeline = ({
  events,
  translate,
  statusKeyMap,
  mediaRefreshKey = 0,
  editable = false,
  orderId = null,
  onAfterMutate = async () => {},
  onDeleteHistoryBlock = null,
}) => {
  const groups = useMemo(() => groupEventsByDay(events || []), [events]);
  const [editOpen, setEditOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [editNote, setEditNote] = useState('');
  const [editDateTime, setEditDateTime] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const openEdit = useCallback((ev) => {
    setEditEvent(ev);
    setEditNote(ev.note != null ? String(ev.note) : '');
    setEditDateTime(apiDateTimeToDatetimeLocal(ev.created_at) || '');
    setEditOpen(true);
  }, []);

  const closeEdit = useCallback(() => {
    setEditOpen(false);
    setEditEvent(null);
    setEditNote('');
    setEditDateTime('');
  }, []);

  const saveEdit = useCallback(async () => {
    if (!orderId || !editEvent?.id) return;
    const createdAtApi = datetimeLocalToApi(editDateTime);
    if (!createdAtApi) {
      setSnackbar({
        open: true,
        message: translate('orders.history_edit_invalid_datetime'),
        severity: 'error',
      });
      return;
    }
    setEditSaving(true);
    try {
      await patchOrderStatusHistory(orderId, editEvent.id, {
        note: editNote,
        created_at: createdAtApi,
      });
      await onAfterMutate();
      setSnackbar({
        open: true,
        message: translate('orders.history_edit_saved'),
        severity: 'success',
      });
      closeEdit();
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.message || translate('orders.history_edit_error'),
        severity: 'error',
      });
    } finally {
      setEditSaving(false);
    }
  }, [orderId, editEvent, editNote, editDateTime, translate, onAfterMutate, closeEdit]);

  if (groups.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        {translate('orders.timeline_empty')}
      </Typography>
    );
  }

  const labelFor = (status, ev, role) => {
    if (role === 'prev' && ev.previous_status_label) return ev.previous_status_label;
    if (role === 'cur' && ev.status_label) return ev.status_label;
    return translate(statusKeyMap[status] || 'orders.status');
  };

  return (
    <>
      <Box
        sx={(theme) => ({
          position: 'relative',
          pl: '20px',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: '5px',
            top: '8px',
            bottom: '8px',
            width: '2px',
            bgcolor: 'divider',
            borderRadius: '1px',
          },
        })}
      >
        {groups.map((group) => {
          const dayLabel = format(group.zoned, "d 'de' MMMM 'de' yyyy", { locale: ptBR });
          const weekday = format(group.zoned, 'EEEE', { locale: ptBR });
          return (
            <Box key={format(group.zoned, 'yyyy-MM-dd')} sx={{ mb: { xs: '16px', md: '28px' } }}>
              <Box
                sx={(theme) => ({
                  position: 'relative',
                  mb: { xs: '8px', md: '14px' },
                  pl: '18px',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: '-17px',
                    top: '6px',
                    width: '11px',
                    height: '11px',
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    border: `2px solid ${theme.palette.background.paper}`,
                    boxShadow: `0 0 0 1px ${theme.palette.divider}`,
                  },
                })}
              >
                <Typography component="span" sx={{ fontWeight: 600, fontSize: '15px', color: 'text.primary' }}>
                  {dayLabel}
                </Typography>
                <Typography component="span" sx={{ fontWeight: 500, fontSize: '12px', color: 'text.secondary', ml: 1 }}>
                  {weekday}
                </Typography>
              </Box>
              <Box sx={dayEntriesSx}>
                {group.events.map((ev, idx) => {
                  const timeStr = format(ev._zoned, 'HH:mm');
                  const { legacyKeys, changes } = parseFieldChanges(ev);
                  const isFieldEdit = Boolean((changes && changes.length) || (legacyKeys && legacyKeys.length));
                  const title = isFieldEdit
                    ? ''
                    : ev.previous_status
                      ? `${labelFor(ev.previous_status, ev, 'prev')} → ${labelFor(ev.status, ev, 'cur')}`
                      : labelFor(ev.status, ev, 'cur');
                  const key = ev.id ? ev.id : `${ev.created_at}-${idx}`;
                  const canEditCard = Boolean(editable && orderId && ev.id);
                  return (
                    <Box
                      key={key}
                      sx={(theme) => ({
                        position: 'relative',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        p: '14px 16px',
                        boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.2 : 0.06)}`,
                        borderLeft: `3px solid ${isFieldEdit ? theme.palette.secondary.main : theme.palette.primary.main}`,
                      })}
                    >
                      {canEditCard && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            display: 'flex',
                            gap: 0.25,
                          }}
                        >
                          <IconButton size="small" aria-label={translate('orders.history_edit_block')} onClick={() => openEdit(ev)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {onDeleteHistoryBlock && (
                            <IconButton
                              size="small"
                              aria-label={translate('orders.history_delete_block')}
                              onClick={() => onDeleteHistoryBlock(ev.id)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: '8px 12px',
                          mb: '8px',
                          fontSize: '12px',
                          color: 'text.secondary',
                          pr: canEditCard ? 7 : 0,
                        }}
                      >
                        <Box
                          component="span"
                          sx={(theme) => ({
                            fontSize: '11px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            px: '8px',
                            py: '3px',
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            color: 'primary.dark',
                          })}
                        >
                          {isFieldEdit ? translate('orders.timeline_field_edit_badge') : translate('orders.timeline_status')}
                        </Box>
                        <span>{timeStr}</span>
                        {ev.created_by_email && String(ev.created_by_email).trim() !== '' && (
                          <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                            {translate('orders.history_changed_by')}: {String(ev.created_by_email).trim()}
                          </Typography>
                        )}
                      </Box>
                      {title ? (
                        <Typography sx={{ fontWeight: 600, fontSize: '14px', m: 0, mb: ev.note || ev.id ? '8px' : '6px', pr: canEditCard ? 6 : 0 }}>
                          {title}
                        </Typography>
                      ) : null}
                      {isFieldEdit && (
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, pr: canEditCard ? 6 : 0 }}>
                          {translate('orders.history_field_changes_title')}
                        </Typography>
                      )}
                      {isFieldEdit && changes && changes.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: ev.note || ev.id ? 1 : 0, pr: canEditCard ? 6 : 0 }}>
                          {changes.map((ch) => {
                            const fk = ch.field;
                            const fieldI18n = historyFieldI18nPath(fk);
                            const fieldLabel = fieldI18n ? translate(fieldI18n) : fk;
                            const fromFull = displayHistoryValue(ch.from);
                            const toFull = displayHistoryValue(ch.to);
                            const fromPrev = previewHistoryText(ch.from);
                            const toPrev = previewHistoryText(ch.to);
                            const isPaymentEmpty =
                              fk === 'payment_entries' && String(ch.from || '').trim() === '' && String(ch.to || '').trim() === '';
                            return (
                              <Box key={fk} sx={{ borderLeft: '2px solid', borderColor: 'divider', pl: 1.25 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                  {fieldLabel}
                                </Typography>
                                {isPaymentEmpty ? (
                                  <Typography variant="body2" color="text.secondary">
                                    {translate('orders.history_payment_generic')}
                                  </Typography>
                                ) : (
                                  <>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                      title={fromFull.length > HISTORY_TEXT_PREVIEW ? fromFull : undefined}
                                    >
                                      <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                        {translate('orders.history_change_from')}
                                      </Box>{' '}
                                      {renderTextWithLinks(fromPrev)}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                      title={toFull.length > HISTORY_TEXT_PREVIEW ? toFull : undefined}
                                    >
                                      <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                        {translate('orders.history_change_to')}
                                      </Box>{' '}
                                      {renderTextWithLinks(toPrev)}
                                    </Typography>
                                  </>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                      {isFieldEdit && legacyKeys && legacyKeys.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: ev.note || ev.id ? 1 : 0 }}>
                          {legacyKeys.map((fk) => {
                            const legI18n = historyFieldI18nPath(fk);
                            return (
                              <Chip
                                key={fk}
                                size="small"
                                variant="outlined"
                                label={legI18n ? translate(legI18n) : fk}
                                sx={{ height: 22, fontSize: '0.7rem' }}
                              />
                            );
                          })}
                        </Box>
                      )}
                      {ev.note && String(ev.note).trim() !== '' && (
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mb: ev.id ? 1 : 0 }}>
                          {renderTextWithLinks(ev.note)}
                        </Typography>
                      )}
                      {ev.id && (
                        <Box sx={{ mt: 1 }}>
                          <MediaGallery
                            metadataKey="order_status_history"
                            metadataValue={ev.id}
                            accept="*/*"
                            readOnly
                            hideEmptyReadOnly
                            refreshKey={mediaRefreshKey}
                          />
                        </Box>
                      )}
                      {!ev.id && editable && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {translate('orders.history_inferred_hint')}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Dialog open={editOpen} onClose={() => !editSaving && closeEdit()} fullWidth maxWidth="md">
        <DialogTitle>{translate('orders.history_edit_dialog_title')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label={translate('orders.history_edit_datetime')}
            type="datetime-local"
            value={editDateTime}
            onChange={(e) => setEditDateTime(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label={translate('orders.history_edit_note')}
            value={editNote}
            onChange={(e) => setEditNote(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={3}
            placeholder={translate('orders.history_edit_note_placeholder')}
          />
          {editEvent?.id && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                {translate('orders.attachments')}
              </Typography>
              <MediaGallery
                metadataKey="order_status_history"
                metadataValue={editEvent.id}
                accept="*/*"
                readOnly={false}
                hideEmptyReadOnly={false}
                refreshKey={mediaRefreshKey}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={editSaving}>
            {translate('common.cancel')}
          </Button>
          <Button variant="contained" onClick={saveEdit} disabled={editSaving}>
            {translate('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OrderHistoryTimeline;
